import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { LatenessSettingsService } from '../infra/lateness-settings.service';
import {
  initialLatenessSettingsState,
  LatenessSettings,
  LatenessSettingsRequest,
  LatenessSettingsState,
} from '../entities/lateness-settings.model';

const OPS = ['load', 'update', 'updateNotices'] as const;

export const LatenessSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<LatenessSettingsState>(initialLatenessSettingsState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(LatenessSettingsService),
      errorStore = inject(ErrorStore)
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (latenessSettings: LatenessSettings) =>
                  patchState(store, { latenessSettings }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) =>
                  patchState(store, setPropError('load', e)),
              })
            )
          )
        )
      ),

      update: rxMethod<LatenessSettingsRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (latenessSettings: LatenessSettings) =>
                  patchState(store, { latenessSettings }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              })
            )
          )
        )
      ),

      updateNotices: rxMethod<LatenessSettingsRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('updateNotices'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (latenessSettings: LatenessSettings) =>
                  patchState(store, { latenessSettings }, setPropLoaded('updateNotices')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('updateNotices', e));
                  errorStore.setHttpError(e);
                },
              })
            )
          )
        )
      ),

      blankUpdateNoticesState(): void {
        patchState(store, setPropInit('updateNotices'));
      },

      blankState(): void {
        patchState(store, initialLatenessSettingsState, setPropInit(...OPS));
      },
    })
  )
);
